import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { getUploadUrl } from "../lib/api";
import {
  LayoutDashboard,
  Package,
  Fish,
  Users,
  Truck,
  Building2,
  LogOut,
  Menu,
  X,
  UserCog,
  Ship,
  FolderOpen,
  Settings,
  ChevronDown,
  Check,
} from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { BarChart3 } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { ScrollText } from "lucide-react";

const navItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", roles: ["admin", "admin_consultor", "proveedor", "cliente"] },
  { to: "/batches", icon: Package, label: "Lotes", roles: ["admin", "admin_consultor", "proveedor", "cliente"] },
  { to: "/documents", icon: FolderOpen, label: "Documentos", roles: ["admin", "admin_consultor", "proveedor", "cliente"] },
  { to: "/reports", icon: BarChart3, label: "Informes", roles: ["admin", "admin_consultor", "proveedor", "cliente"] },
  { to: "/products", icon: Fish, label: "Productos", roles: ["admin", "admin_consultor", "proveedor", "cliente"] },
  { to: "/clients", icon: Users, label: "Clientes", roles: ["admin", "admin_consultor"] },
  { to: "/suppliers", icon: Building2, label: "Proveedores", roles: ["admin", "admin_consultor", "proveedor"] },
  { to: "/logistics", icon: Truck, label: "Op. Logísticos", roles: ["admin", "admin_consultor", "proveedor"] },
  { to: "/transport", icon: Ship, label: "Transporte", roles: ["admin", "admin_consultor", "proveedor"] },
  { to: "/users", icon: UserCog, label: "Usuarios", roles: ["admin"] },  // Solo admin, NO admin_consultor
  { to: "/activity-logs", icon: ScrollText, label: "Registro Actividad", roles: ["admin"] },  // Solo admin, NO admin_consultor
  { to: "/configuration", icon: Settings, label: "Configuración", roles: ["admin"] },  // Solo admin, NO admin_consultor
];

export default function Layout() {
  const { user, logout, hasRole } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [empresaActiva, setEmpresaActiva] = useState(null);
  const [empresasDisponibles, setEmpresasDisponibles] = useState([]);
  const [showEmpresaSelector, setShowEmpresaSelector] = useState(false);

  useEffect(() => {
    // Cargar empresa activa para admin, admin_consultor y proveedores
    if (hasRole("admin") || hasRole("admin_consultor") || hasRole("proveedor")) {
      api.get("/config").then(res => {
        const config = res.data;
        if (config.empresas) {
          setEmpresasDisponibles(config.empresas);
        }
        if (config.empresa_activa_id && config.empresas) {
          const empresa = config.empresas.find(e => e.id === config.empresa_activa_id);
          setEmpresaActiva(empresa);
        }
      }).catch(() => {});
    }
  }, [hasRole]);

  const handleCambiarEmpresa = async (empresaId) => {
    try {
      await api.put("/config/empresa-activa", { empresa_activa_id: empresaId });
      const empresa = empresasDisponibles.find(e => e.id === empresaId);
      setEmpresaActiva(empresa);
      setShowEmpresaSelector(false);
      toast.success(`Empresa cambiada a ${empresa?.nombre}`);
      // Recargar la página para actualizar todos los datos
      window.location.reload();
    } catch (error) {
      console.error("Error al cambiar empresa:", error);
      const mensaje = error.response?.data?.detail || "Error al cambiar empresa";
      toast.error(mensaje);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const visibleNavItems = navItems.filter((item) =>
    item.roles.some((role) => hasRole(role))
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        data-testid="app-sidebar"
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 sidebar print-hide transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="p-4 border-b border-slate-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* Mostrar logo de empresa activa o logo por defecto - NO mostrar para clientes */}
                {hasRole("cliente") ? (
                  <div className="w-12 h-12 rounded-lg bg-slate-700 flex items-center justify-center">
                    <Fish className="w-6 h-6 text-emerald-400" />
                  </div>
                ) : empresaActiva?.logo_url ? (
                  <img 
                    src={getUploadUrl(empresaActiva.logo_url)} 
                    alt={empresaActiva.nombre}
                    className="w-12 h-12 rounded-lg object-contain bg-white p-1"
                    onError={(e) => { e.target.src = '/logo-vexpa.png'; }}
                  />
                ) : (
                  <img 
                    src="/logo-vexpa.png" 
                    alt="Premium Fish" 
                    className="w-12 h-12 rounded-full object-cover"
                  />
                )}
                <div>
                  <p className="text-xs text-slate-400">Premium Fish</p>
                  <p className="text-[10px] text-emerald-400 font-medium">Sistema de Gestión</p>
                </div>
              </div>
              <button
                className="lg:hidden text-slate-400 hover:text-white"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Selector de empresa activa (para admin, admin_consultor y proveedor) */}
            {empresaActiva && (hasRole("admin") || hasRole("admin_consultor") || hasRole("proveedor")) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-full mt-3 px-3 py-2 bg-emerald-900/50 border border-emerald-700/50 rounded-lg hover:bg-emerald-800/50 transition-colors text-left">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-emerald-400 font-medium">
                          {hasRole("proveedor") ? "Facturando a" : "Empresa activa"}
                        </p>
                        <p className="text-sm font-semibold text-emerald-100 truncate" title={empresaActiva.nombre}>
                          {empresaActiva.nombre}
                        </p>
                      </div>
                      <ChevronDown className="w-4 h-4 text-emerald-400" />
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  {empresasDisponibles.map((empresa) => (
                    <DropdownMenuItem
                      key={empresa.id}
                      onClick={() => handleCambiarEmpresa(empresa.id)}
                      className="cursor-pointer"
                    >
                      <div className="flex items-center gap-2 w-full">
                        {empresa.id === empresaActiva?.id && (
                          <Check className="w-4 h-4 text-emerald-600" />
                        )}
                        {empresa.id !== empresaActiva?.id && (
                          <div className="w-4 h-4" />
                        )}
                        <span className="truncate">{empresa.nombre}</span>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {/* Mostrar empresa activa (solo lectura) para admin_consultor */}
            {empresaActiva && hasRole("admin_consultor") && (
              <div className="w-full mt-3 px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">
                  Empresa activa (solo lectura)
                </p>
                <p className="text-sm font-semibold text-slate-200 truncate" title={empresaActiva.nombre}>
                  {empresaActiva.nombre}
                </p>
              </div>
            )}
          </div>

          <nav className="flex-1 p-4 space-y-1" data-testid="main-navigation">
            {visibleNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? "active" : ""}`
                }
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="p-4 border-t border-slate-700">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                <span className="text-sm font-medium text-white">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-slate-400 capitalize">
                  {user?.role === "admin" ? "Administrador" : 
                   user?.role === "admin_consultor" ? "Admin Consultor" :
                   user?.role === "proveedor" ? "Proveedor" : "Cliente"}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar sesión
            </Button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 print:ml-0">
        <header className="glass-header print-hide sticky top-0 z-30 h-16 flex items-center px-6 lg:hidden">
          <button
            className="p-2 text-slate-600 hover:text-slate-900"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-button"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="ml-4 flex items-center gap-2">
            <img 
              src="/logo-vexpa.png" 
              alt="VEXPA" 
              className="w-8 h-8 rounded-full object-cover"
            />
            <span className="font-bold text-slate-900">VEXPA</span>
          </div>
        </header>

        <main className="flex-1 p-6 md:p-8 overflow-auto print:p-0 print:overflow-visible">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
